import React, { useCallback, useRef, useState } from "react";
import { AppShell } from "@/components/AppShell";
import { PHIWarning } from "@/components/PHIWarning";
import { Button } from "@/components/ui/button";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  UploadCloud, FileSpreadsheet, FileText, AlertTriangle, CheckCircle2, X, Loader2, Download as DownloadIcon,
} from "lucide-react";

const ACCEPTED = [".csv", ".xlsx", ".xls"];

export default function UploadPage() {
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const [isDragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [phiBlock, setPhiBlock] = useState(null);
  const [success, setSuccess] = useState(null);
  const [vertical, setVertical] = useState("");
  const [uploadError, setUploadError] = useState(null);
  const [submittedCount, setSubmittedCount] = useState("");
  const [periodLabel, setPeriodLabel] = useState("");

  const handleFiles = useCallback(async (file) => {
    if (!file) return;
    if (!vertical) {
      setUploadError("Please select a claims specialty above (Hospital, Professional Services, or Behavioral Health) before uploading.");
      toast.error("Select your claims specialty first.");
      // Scroll the specialty card into view so the user can't miss it.
      document.querySelector('[data-testid="specialty-selector-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const name = file.name.toLowerCase();
    if (!ACCEPTED.some((e) => name.endsWith(e))) {
      setUploadError(`Only CSV or Excel files are accepted. Got "${file.name}". Accepted extensions: ${ACCEPTED.join(", ")}.`);
      toast.error("Only CSV or Excel files are accepted.");
      return;
    }
    setUploading(true); setPhiBlock(null); setSuccess(null); setUploadError(null);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("vertical", vertical);
    // Optional context — unlocks Denial Rate + First Pass % on the dashboard.
    // Server skips Denial Rate / First Pass cards entirely when these are
    // missing, so the customer never sees a fake "100%".
    const submittedNum = parseInt(submittedCount, 10);
    if (Number.isFinite(submittedNum) && submittedNum > 0) {
      fd.append("submitted_claims_count", String(submittedNum));
    }
    if (periodLabel.trim()) {
      fd.append("period_label", periodLabel.trim());
    }
    try {
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.phi_detected) {
        setPhiBlock(data);
      } else if (data.accepted) {
        setSuccess({
          rows: data.rows_loaded,
          name: data.dataset?.name,
          mismatch: data.specialty_mismatch_warning || null,
          detected: data.specialty_detection?.detected || null,
        });
        toast.success(`${data.rows_loaded} rows loaded.`);
      }
    } catch (err) {
      // HTTP 422 PHI hard-reject — server now returns the rejection as an error
      // status. The detail body keeps the legacy `accepted:false` shape so we
      // can still render the red PHI banner instead of a generic toast.
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.phi_detected) {
        setPhiBlock(detail);
        // Suppress the generic toast — the banner says everything.
      } else {
        const msg = typeof detail === "string" ? detail : (detail?.message || "Upload failed");
        setUploadError(msg);
        toast.error(String(msg).split("\n")[0]);
      }
    } finally {
      setUploading(false);
    }
  }, [vertical]);

  const onDrop = (e) => {
    e.preventDefault(); setDragging(false);
    const f = e.dataTransfer.files?.[0];
    handleFiles(f);
  };

  const loadDemo = async () => {
    if (!vertical) {
      toast.error("Please select your claims specialty above before loading the demo.");
      return;
    }
    setDemoLoading(true); setPhiBlock(null); setSuccess(null);
    try {
      const { data } = await api.post("/demo/load", { vertical });
      setSuccess({ rows: data.rows_loaded, name: data.dataset?.name, demo: true });
      toast.success("Demo dataset loaded.");
      setTimeout(() => navigate("/dashboard"), 800);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to load demo");
    } finally {
      setDemoLoading(false);
    }
  };

  const verticals = [
    { id: "hospital", label: "Hospital", desc: "Inpatient & outpatient facility claims" },
    { id: "professional", label: "Professional Services", desc: "Physician group billing & E&M" },
    { id: "behavioral", label: "Behavioral Health", desc: "Mental health & SUD outpatient" },
  ];

  return (
    <AppShell>
      <div className="max-w-5xl mx-auto space-y-6 fade-up">
        <div>
          <span className="label-overline">Step 1 · Data Upload</span>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold text-slate-900 mt-2 tracking-tight">
            Upload De-Identified Denial Data
          </h1>
          <p className="text-slate-500 mt-2 max-w-2xl">
            Upload CSV or Excel denial data. Denial intelligence, AI insights, and appeal language automatically adapt to the claims specialty you select. All uploads are scanned to prevent PHI exposure.
          </p>
        </div>

        <PHIWarning />

        {/* Step 1A — pick the specialty so all downstream intelligence adapts */}
        <div className="surface-card p-5" data-testid="specialty-selector-card">
          <div className="flex flex-wrap items-end justify-between gap-3 mb-4">
            <div>
              <span className="label-overline">Step 1a · Claims Specialty</span>
              <h3 className="font-display text-lg font-semibold text-slate-900 mt-1">
                Tell us the type of claims you're uploading
              </h3>
              <p className="text-xs text-slate-500 mt-1">
                Denial intelligence, AI insights, and appeal language adapt to the specialty you pick.
                We'll cross-check it against the data after upload.
              </p>
            </div>
            {vertical && (
              <span
                data-testid="specialty-selected-chip"
                className="text-[11px] font-semibold uppercase tracking-wider text-teal-700 bg-teal-50 border border-teal-200 px-2 py-1 rounded-md"
              >
                Selected: {verticals.find((v) => v.id === vertical)?.label}
              </span>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-2.5">
            {verticals.map((v) => (
              <button
                key={v.id}
                type="button"
                onClick={() => setVertical(v.id)}
                data-testid={`upload-specialty-${v.id}-button`}
                className={`text-left rounded-xl border px-4 py-3 transition-all ${
                  vertical === v.id
                    ? "border-[#0F4C81] bg-soft-blue ring-1 ring-[#0F4C81]/30"
                    : "border-slate-200 hover:border-cyan-300 bg-white"
                }`}
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="text-sm font-semibold text-slate-900">{v.label}</div>
                    <div className="text-xs text-slate-500 mt-0.5">{v.desc}</div>
                  </div>
                  <div className={`h-4 w-4 rounded-full border-2 mt-0.5 transition-all shrink-0 ${
                    vertical === v.id ? "border-[#0F4C81] bg-[#0F4C81]" : "border-slate-300"
                  }`} />
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* Optional context — unlocks Denial Rate + First Pass Acceptance on the dashboard */}
        <div className="surface-card p-5" data-testid="upload-period-context-card">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div>
              <div className="text-sm font-bold uppercase tracking-wider text-slate-900">Optional · Unlock Denial Rate</div>
              <p className="text-xs text-slate-600 mt-1 max-w-2xl">
                If you uploaded only your denied claims, your dataset is missing the denominator needed to calculate
                <span className="font-semibold"> Denial Rate</span> and
                <span className="font-semibold"> First Pass Acceptance %</span>.
                Tell us the total claims submitted for this period and we&apos;ll compute both accurately. Skip if you don&apos;t have the number — we&apos;ll just hide those two metrics rather than show a fake 100%.
              </p>
            </div>
            <span className="hidden sm:inline-block text-[10px] font-bold uppercase tracking-[0.18em] text-teal-700 bg-teal-50 border border-teal-200 rounded-full px-2 py-1 whitespace-nowrap">Optional</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-slate-700">Total claims submitted for this period</label>
              <input
                type="number" min="1" step="1" inputMode="numeric"
                value={submittedCount}
                onChange={(e) => setSubmittedCount(e.target.value)}
                placeholder="e.g., 5400"
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm bg-white"
                data-testid="upload-submitted-count"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Used as the denominator: Denial Rate = Denied claims ÷ Submitted claims × 100
              </p>
            </div>
            <div>
              <label className="text-xs font-semibold text-slate-700">Period covered (label)</label>
              <input
                type="text" maxLength={80}
                value={periodLabel}
                onChange={(e) => setPeriodLabel(e.target.value)}
                placeholder="e.g., Jan – Mar 2026"
                className="mt-1 h-10 w-full rounded-lg border border-slate-300 px-3 text-sm bg-white"
                data-testid="upload-period-label"
              />
              <p className="text-[10px] text-slate-500 mt-1">
                Shows on the dashboard + every exported PDF report.
              </p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
          {/* Dropzone */}
          <div className="surface-card p-2">
            <div
              data-testid="file-upload-dropzone"
              onDragOver={(e) => { if (vertical && !uploading) { e.preventDefault(); setDragging(true); } }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
              onClick={() => { if (!vertical) { handleFiles(null); /* triggers specialty hint */ document.querySelector('[data-testid="specialty-selector-card"]')?.scrollIntoView({ behavior: "smooth", block: "center" }); return; } if (uploading) return; inputRef.current?.click(); }}
              className={`rounded-lg border-2 border-dashed transition-all duration-200 p-12 text-center
                ${!vertical ? "border-slate-200 bg-slate-50/40 cursor-not-allowed opacity-70" :
                  isDragging ? "border-[#14B8A6] bg-cyan-50/60 cursor-pointer" :
                  "border-slate-300 bg-slate-50/50 hover:border-cyan-400 hover:bg-cyan-50/40 cursor-pointer"}`}
              aria-disabled={!vertical}
            >
              <div className="mx-auto h-14 w-14 rounded-2xl bg-soft-blue flex items-center justify-center mb-4">
                <UploadCloud className="h-7 w-7 text-[#0F4C81]" />
              </div>
              <div className="font-display text-xl font-semibold text-slate-900">
                {uploading ? "Scanning for PHI..." : !vertical ? "Select a Claims Specialty First" : "Drag & Drop Your File Here"}
              </div>
              <p className="text-sm text-slate-500 mt-2">
                {!vertical
                  ? "Pick Hospital, Professional Services, or Behavioral Health above to enable upload."
                  : "or click to browse — accepted: CSV, XLSX, XLS"}
              </p>
              <input
                ref={inputRef}
                type="file"
                accept=".csv,.xlsx,.xls"
                data-testid="file-upload-input"
                onChange={(e) => handleFiles(e.target.files?.[0])}
                className="hidden"
              />
              {uploading && (
                <div className="mt-6 flex items-center justify-center gap-2 text-sm text-[#0F4C81]">
                  <Loader2 className="h-4 w-4 animate-spin" /> Validating fields & scanning content
                </div>
              )}
            </div>

            {phiBlock && (
              <div data-testid="phi-rejection-banner" className="mt-4 rounded-xl border border-red-200 bg-red-50 p-5">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-red-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-5 w-5 text-red-700" />
                  </div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-red-800">Upload Rejected — PHI Detected</div>
                    <p className="text-sm text-red-900 mt-1.5 leading-relaxed">
                      This file may contain PHI or patient-identifiable information. Please remove all PHI before uploading. Only de-identified operational revenue cycle data is accepted.
                    </p>
                    {phiBlock.flagged_columns?.length > 0 && (
                      <div className="mt-3">
                        <div className="text-[11px] font-semibold uppercase tracking-wider text-red-700">Flagged Columns</div>
                        <div className="flex flex-wrap gap-1.5 mt-1">
                          {phiBlock.flagged_columns.map((c) => (
                            <span key={c} className="text-xs font-mono px-2 py-0.5 rounded bg-red-100 text-red-800 border border-red-200">{c}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    {phiBlock.reasons?.length > 0 && (
                      <ul className="mt-3 space-y-1 text-xs text-red-800 list-disc list-inside">
                        {phiBlock.reasons.slice(0, 5).map((r, i) => <li key={i}>{r}</li>)}
                      </ul>
                    )}
                  </div>
                  <button onClick={() => setPhiBlock(null)} className="text-red-700 hover:text-red-900"><X className="h-4 w-4" /></button>
                </div>
              </div>
            )}

            {uploadError && !phiBlock && !success && (
              <div data-testid="upload-error-banner" className="mt-4 rounded-xl border border-amber-300 bg-amber-50 p-5">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-lg bg-amber-100 flex items-center justify-center shrink-0">
                    <AlertTriangle className="h-5 w-5 text-amber-700" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-900">Upload Couldn't Be Processed</div>
                    <pre
                      className="text-[12.5px] text-amber-900 mt-2 leading-relaxed whitespace-pre-wrap break-words font-sans"
                      data-testid="upload-error-detail"
                    >
                      {uploadError}
                    </pre>
                  </div>
                  <button onClick={() => setUploadError(null)} className="text-amber-700 hover:text-amber-900 shrink-0">
                    <X className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}

            {success && (
              <div data-testid="upload-success-banner" className="mt-4 rounded-xl border border-emerald-200 bg-emerald-50 p-5">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-emerald-100 flex items-center justify-center"><CheckCircle2 className="h-5 w-5 text-emerald-700" /></div>
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-emerald-800">{success.demo ? "Demo loaded" : "Upload accepted"}</div>
                    <div className="text-sm text-emerald-900 mt-0.5">{success.rows.toLocaleString()} rows loaded · {success.name}</div>
                  </div>
                  <Button data-testid="goto-dashboard-button" onClick={() => navigate("/dashboard")} className="bg-navy hover:bg-[#0B3A66] text-white">
                    View Dashboard
                  </Button>
                </div>
                {success.mismatch && (
                  <div data-testid="specialty-mismatch-banner" className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3.5 flex items-start gap-3">
                    <AlertTriangle className="h-4 w-4 text-amber-700 mt-0.5 shrink-0" />
                    <div className="text-xs text-amber-900 leading-relaxed">
                      <span className="font-semibold">Specialty check:</span> {success.mismatch}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Side panel */}
          <div className="space-y-4">
            <div className="surface-card p-6">
              <div className="flex items-center gap-2 mb-2">
                <FileSpreadsheet className="h-4 w-4 text-slate-600" />
                <span className="label-overline">Accepted Fields</span>
              </div>
              <ul className="mt-2 space-y-1.5 text-sm text-slate-600">
                {[
                  "ANSI / CARC / Adjustment Reason Code (required *)",
                  "payor / payer / insurance / carrier (required)",
                  "* OR a denial_category / denial description / RARC code",
                  "claim_status (Paid / Denied / Clean Claim / First-Pass) — auto-classifies rows",
                  "denial_amount (optional — defaults to 0 if missing)",
                  "account_number / claim_id / encounter_id (de-identified)",
                  "RARC / Remark / Remittance Code",
                  "denial_category OR denial description text",
                  "facility · cpt_code · revenue_code",
                  "aging_bucket · aging_days",
                  "service_month",
                ].map((f) => (<li key={f} className="flex items-start gap-2"><span className="text-[#14B8A6] mt-0.5">▸</span>{f}</li>))}
              </ul>
              <div className="mt-3 rounded-lg border border-teal-200 bg-teal-50/60 px-3 py-2.5 text-[12.5px] text-slate-700 leading-snug">
                <span className="font-semibold text-teal-800">Tip</span> — upload <span className="font-semibold">all submitted claims</span> (paid + denied) for exact denial rate &amp; first-pass acceptance. Either include a <code className="text-[11.5px]">claim_status</code> column, or leave the ANSI code blank on paid rows.
              </div>

              {/* Client onboarding kit — downloadable CSV template + field guide PDF */}
              <div className="mt-3 rounded-lg border border-navy/20 bg-slate-50 p-3.5" data-testid="client-onboarding-kit">
                <div className="flex items-center gap-1.5 text-[10.5px] uppercase tracking-[0.16em] font-semibold text-navy mb-1.5">
                  <FileText className="h-3 w-3" /> Client Onboarding Kit
                </div>
                <p className="text-[12px] text-slate-600 leading-snug mb-2.5">
                  Share these with your clients to standardize their denial file format.
                </p>
                <div className="flex flex-col gap-1.5">
                  <a
                    href="/RCIQ_Denial_Template.csv"
                    download
                    data-testid="download-template-csv"
                    className="inline-flex items-center gap-2 text-[12px] font-semibold text-teal-700 hover:text-teal-900 transition-colors"
                  >
                    <DownloadIcon className="h-3.5 w-3.5" /> Download CSV Template
                    <span className="text-[10.5px] text-slate-500 font-normal">· 12 sample rows · all 15 columns</span>
                  </a>
                  <a
                    href="/RCIQ_Denial_Template_Guide.pdf"
                    download
                    data-testid="download-template-guide"
                    className="inline-flex items-center gap-2 text-[12px] font-semibold text-teal-700 hover:text-teal-900 transition-colors"
                  >
                    <DownloadIcon className="h-3.5 w-3.5" /> Download Field Guide PDF
                    <span className="text-[10.5px] text-slate-500 font-normal">· 4 pages · field-by-field explanations</span>
                  </a>
                </div>
              </div>
              <p className="text-[11px] text-slate-500 mt-3 leading-relaxed">
                Denial category is auto-derived from CARC/RARC codes. Codes are normalized — <span className="font-mono text-slate-700">CO197</span>, <span className="font-mono text-slate-700">CO-197</span>, <span className="font-mono text-slate-700">197</span>, and <span className="font-mono text-slate-700">"CARC 197"</span> all map to the same canonical <span className="font-mono font-semibold text-teal-700">CO-197</span> with the official ANSI description.
                {" "}<a href="/ansi-codes" data-testid="upload-ansi-library-link" className="text-teal-700 font-semibold hover:underline">Browse all 308 codes →</a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </AppShell>
  );
}
